
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

/**
 * Retrieves notifications for the current user
 */
export const getUserNotifications = async (userId: string) => {
  try {
    console.log('[notificationsUtils] Getting notifications for user:', userId);
    
    if (!userId) {
      console.error('[notificationsUtils] No userId provided');
      return [];
    }
    
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('[notificationsUtils] Error getting notifications:', error);
      return [];
    }
    
    // Ensure the is_read property is properly mapped from the database is_read field
    const processedNotifications = data?.map(notification => ({
      ...notification,
      is_read: notification.is_read === true
    })) || [];
    
    console.log(`[notificationsUtils] Retrieved ${processedNotifications.length || 0} notifications`);
    return processedNotifications;
  } catch (error) {
    console.error('[notificationsUtils] Unexpected error getting notifications:', error);
    return [];
  }
};

/**
 * Marks a notification as read
 */
export const markNotificationAsRead = async (notificationId: string) => {
  try {
    console.log('[notificationsUtils] Marking notification as read:', notificationId);
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', notificationId);
    
    if (error) {
      console.error('[notificationsUtils] Error marking notification as read:', error);
      toast({
        title: "Error",
        description: "Error al marcar la notificación como leída",
        variant: "destructive"
      });
      return false;
    }
    
    console.log('[notificationsUtils] Successfully marked notification as read');
    return true;
  } catch (error) {
    console.error('[notificationsUtils] Error marking notification as read:', error);
    toast({
      title: "Error",
      description: "Error al marcar la notificación como leída",
      variant: "destructive"
    });
    return false;
  }
};

/**
 * Mark all notifications as read for a user
 */
export const markAllNotificationsAsRead = async (userId: string) => {
  try {
    console.log('[notificationsUtils] Marking all notifications as read for user:', userId);
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('user_id', userId);
    
    if (error) {
      console.error('[notificationsUtils] Error marking all notifications as read:', error);
      toast({
        title: "Error",
        description: "Error al marcar todas las notificaciones como leídas",
        variant: "destructive"
      });
      return false;
    }
    
    console.log('[notificationsUtils] Successfully marked all notifications as read');
    toast({
      title: "Éxito",
      description: "Todas las notificaciones han sido marcadas como leídas",
      notificationType: "system"
    });
    return true;
  } catch (error) {
    console.error('[notificationsUtils] Error marking all notifications as read:', error);
    toast({
      title: "Error",
      description: "Error al marcar todas las notificaciones como leídas",
      variant: "destructive"
    });
    return false;
  }
};

/**
 * Delete a notification
 */
export const deleteNotification = async (notificationId: string) => {
  try {
    console.log('[notificationsUtils] Deleting notification:', notificationId);
    
    // For debugging, let's check if the notification exists before attempting to delete
    const { data: checkData, error: checkError } = await supabase
      .from('notifications')
      .select('id, type')
      .eq('id', notificationId)
      .single();
    
    if (checkError) {
      console.error('[notificationsUtils] Error checking notification existence:', checkError);
      toast({
        title: "Error",
        description: "No se pudo encontrar la notificación",
        variant: "destructive"
      });
      return false;
    }
    
    if (!checkData) {
      console.error('[notificationsUtils] Notification not found:', notificationId);
      toast({
        title: "Error",
        description: "No se pudo encontrar la notificación",
        variant: "destructive"
      });
      return false;
    }
    
    console.log('[notificationsUtils] Found notification to delete:', checkData);
    
    // Delete the notification
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', notificationId);
    
    if (error) {
      console.error('[notificationsUtils] Error deleting notification:', error);
      toast({
        title: "Error",
        description: "Error al eliminar la notificación",
        variant: "destructive"
      });
      return false;
    }
    
    console.log('[notificationsUtils] Successfully deleted notification');
    toast({
      title: "Éxito",
      description: "Notificación eliminada correctamente",
      notificationType: checkData.type || "system"
    });
    return true;
  } catch (error) {
    console.error('[notificationsUtils] Unexpected error deleting notification:', error);
    toast({
      title: "Error",
      description: "Error al eliminar la notificación",
      variant: "destructive"
    });
    return false;
  }
};

// Valid notification types allowed by database constraint
const VALID_NOTIFICATION_TYPES = ['system', 'recipe', 'user', 'admin', 'friendship'];

/**
 * Send notification to all users
 */
export const sendNotificationToAllUsers = async (title: string, message: string, type: string = 'admin', link_url?: string) => {
  try {
    // Additional validation to ensure proper data
    if (!title || title.trim().length < 3) {
      console.error('El título debe tener al menos 3 caracteres');
      return false;
    }
    
    if (!message || message.trim().length < 10) {
      console.error('El mensaje debe tener al menos 10 caracteres');
      return false;
    }
    
    // Validate notification type
    if (!VALID_NOTIFICATION_TYPES.includes(type)) {
      console.error(`Tipo de notificación inválido: ${type}. Tipos válidos: ${VALID_NOTIFICATION_TYPES.join(', ')}`);
      throw new Error(`Tipo de notificación inválido: ${type}`);
    }
    
    // Get all users
    const { data: users, error: usersError } = await supabase
      .from('profiles')
      .select('id');
    
    if (usersError) {
      console.error('Error al obtener usuarios:', usersError);
      return false;
    }
    
    if (!users || users.length === 0) {
      console.error('No se encontraron usuarios para enviar notificaciones');
      return false;
    }
    
    // Create notifications for all users
    const notifications = users.map(user => ({
      user_id: user.id,
      title: title.trim(),
      message: message.trim(),
      type,
      is_read: false,
      link_url: link_url || null
    }));
    
    // Insert all notifications in a single transaction
    const { error: insertError } = await supabase
      .from('notifications')
      .insert(notifications);
    
    if (insertError) {
      console.error('Error al enviar notificaciones:', insertError);
      throw new Error(insertError.message);
    }
    
    return true;
  } catch (error) {
    console.error('Error al enviar notificaciones:', error);
    throw error; // Re-throw to be caught in the component
  }
};

/**
 * Send notification to a specific user
 */
export const sendNotificationToUser = async (
  userId: string, 
  title: string, 
  message: string, 
  type: string = 'system',
  relatedId?: string,
  link_url?: string
) => {
  try {
    // Validate notification type
    if (!VALID_NOTIFICATION_TYPES.includes(type)) {
      console.error(`Tipo de notificación inválido: ${type}`);
      return false;
    }
    
    const notification = {
      user_id: userId,
      title: title.trim(),
      message: message.trim(),
      type,
      is_read: false,
      related_id: relatedId || null,
      link_url: link_url || null
    };
    
    const { error } = await supabase
      .from('notifications')
      .insert([notification]);
      
    if (error) {
      console.error('Error al enviar notificación:', error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error al enviar notificación:', error);
    return false;
  }
};

/**
 * Create a friendship request notification
 */
export const createFriendRequestNotification = async (
  recipientId: string,
  requesterId: string,
  requesterName: string
) => {
  try {
    console.log('Creating friendship request notification', {
      recipientId,
      requesterId,
      requesterName
    });
    
    // First, check if a notification already exists for this friendship request
    const { data: existingNotifications, error: checkError } = await supabase
      .from('notifications')
      .select('id')
      .eq('user_id', recipientId)
      .eq('type', 'friendship')
      .eq('related_id', requesterId)
      .eq('is_read', false);
    
    if (checkError) {
      console.error('Error checking existing notifications:', checkError);
    } else if (existingNotifications && existingNotifications.length > 0) {
      console.log('Notification already exists for this friendship request');
      return true; // Notification already exists
    }
    
    // Create new notification
    const notification = {
      user_id: recipientId,
      title: 'Nueva solicitud de amistad',
      message: `${requesterName} te ha enviado una solicitud de amistad`,
      type: 'friendship',
      is_read: false,
      related_id: requesterId,
      link_url: null
    };
    
    const { error } = await supabase
      .from('notifications')
      .insert([notification]);
      
    if (error) {
      console.error('Error al crear notificación de amistad:', error);
      return false;
    }
    
    console.log('Friendship notification created successfully');
    return true;
  } catch (error) {
    console.error('Error al crear notificación de amistad:', error);
    return false;
  }
};
